import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} id - Variant ID
 * @property {string} [title] - Variant title
 * @property {boolean} available - Whether variant is available
 * @property {string[]} options - Selected option names in product option order
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {ProductVariant[] | null} */
  #variantData = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.addEventListener('change', this.#handleStickyOptionChange, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Handles the add to cart button click in the sticky bar.
   * Immediately before the click, it writes the variant selected in the sticky
   * bar into the main product form. This prevents the first variant from being
   * added when the main picker has not finished re-rendering.
   */
  handleAddToCartClick = async () => {
    const selectedVariant = this.#getSelectedStickyVariant();

    if (selectedVariant) {
      this.#applyVariantToProductForm(selectedVariant);
      if (!selectedVariant.available) return;
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (!this.#targetAddToCartButton) return;

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles a selection made in the sticky bar.
   * The selected option names are converted to an exact variant ID, which is
   * written directly to the main product form. The visible main picker is also
   * synchronized where possible so its image, price and availability can update.
   * @param {Event} event
   */
  #handleStickyOptionChange = (event) => {
    const stickySelect = event.target;

    if (!(stickySelect instanceof HTMLSelectElement)) return;
    if (!stickySelect.matches('[data-sticky-option-select]')) return;

    const selectedVariant = this.#getSelectedStickyVariant();
    if (selectedVariant) {
      this.#applyVariantToProductForm(selectedVariant);
      this.#updateStickyVariantTitle(selectedVariant);
    }

    this.#syncMainVariantPicker(stickySelect);
  };

  /**
   * Handles variant update events.
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#updateButtonText();
  };

  /**
   * Handles variant selected events.
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable.
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag.
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events.
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Returns the variants embedded by product-information.liquid.
   * @returns {ProductVariant[]}
   */
  #getVariantData() {
    if (this.#variantData) return this.#variantData;

    const script = this.querySelector('script[data-sticky-variant-data]');
    if (!script?.textContent) {
      this.#variantData = [];
      return this.#variantData;
    }

    try {
      const parsed = JSON.parse(script.textContent);
      this.#variantData = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Unable to parse sticky variant data', error);
      this.#variantData = [];
    }

    return this.#variantData;
  }

  /**
   * Finds the exact variant represented by all sticky select values.
   * @returns {ProductVariant | null}
   */
  #getSelectedStickyVariant() {
    const selects = Array.from(this.querySelectorAll('[data-sticky-option-select]')).filter(
      (element) => element instanceof HTMLSelectElement
    );

    if (selects.length === 0) {
      const currentVariantId = this.dataset.currentVariantId;
      return this.#getVariantData().find((variant) => String(variant.id) === String(currentVariantId)) ?? null;
    }

    selects.sort(
      (a, b) =>
        Number.parseInt(a.dataset.optionPosition || '0', 10) -
        Number.parseInt(b.dataset.optionPosition || '0', 10)
    );

    const selectedOptions = selects.map((select) => select.value);

    return (
      this.#getVariantData().find(
        (variant) =>
          Array.isArray(variant.options) &&
          variant.options.length === selectedOptions.length &&
          variant.options.every((optionName, index) => String(optionName) === String(selectedOptions[index]))
      ) ?? null
    );
  }

  /**
   * Writes a variant ID directly into the main product form.
   * This is the decisive step that guarantees the selected sticky option is the
   * variant submitted to /cart/add.
   * @param {ProductVariant} variant
   */
  #applyVariantToProductForm(variant) {
    const variantId = String(variant.id);
    const available = Boolean(variant.available);

    this.dataset.currentVariantId = variantId;
    this.dataset.variantAvailable = available ? 'true' : 'false';
    this.refs.addToCartButton.disabled = !available;

    const productForm = this.#getProductForm();
    if (!productForm) return;

    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    const form = this.#targetAddToCartButton?.closest('form') ?? productForm.querySelector('form');
    const searchRoot = form ?? productForm;
    const variantInputs = Array.from(searchRoot.querySelectorAll('[name="id"]'));

    for (const control of variantInputs) {
      if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) continue;
      control.value = variantId;
      control.setAttribute('value', variantId);
      control.dispatchEvent(new Event('input', { bubbles: true }));
      control.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.disabled = !available;
      this.#targetAddToCartButton.dataset.variantId = variantId;
    }
  }

  /**
   * Synchronizes the visible main variant picker when possible.
   * Cart correctness does not depend on this method; the product form is already
   * updated directly by #applyVariantToProductForm.
   * @param {HTMLSelectElement} stickySelect
   */
  #syncMainVariantPicker(stickySelect) {
    const variantPicker = this.#getVariantPicker();
    if (!variantPicker) return;

    const optionIndex = Math.max(Number.parseInt(stickySelect.dataset.optionPosition || '1', 10) - 1, 0);
    const selectedOption = stickySelect.selectedOptions[0];
    const optionValueId = selectedOption?.dataset.optionValueId || '';
    const optionName = stickySelect.value;

    const fieldsets = Array.from(variantPicker.querySelectorAll('fieldset'));
    const selects = Array.from(variantPicker.querySelectorAll('select'));

    if (fieldsets[optionIndex]) {
      const controls = Array.from(fieldsets[optionIndex].querySelectorAll('input'));
      const matchingInput = controls.find(
        (control) =>
          control.dataset.optionValueId === optionValueId ||
          control.value === optionName ||
          control.getAttribute('value') === optionName
      );

      if (matchingInput instanceof HTMLInputElement && !matchingInput.disabled && !matchingInput.checked) {
        matchingInput.click();
      }
      return;
    }

    const mainSelect = selects[optionIndex];
    if (mainSelect instanceof HTMLSelectElement) {
      const matchingOption = Array.from(mainSelect.options).find(
        (option) =>
          option.dataset.optionValueId === optionValueId ||
          option.value === optionName ||
          option.textContent?.trim() === optionName
      );

      if (!matchingOption || matchingOption.disabled) return;
      if (mainSelect.value === matchingOption.value && matchingOption.selected) return;

      mainSelect.value = matchingOption.value;
      mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const fallbackControl = Array.from(
      variantPicker.querySelectorAll('input[data-option-value-id], option[data-option-value-id]')
    ).find(
      (control) =>
        control.dataset.optionValueId === optionValueId ||
        (control instanceof HTMLInputElement && control.value === optionName) ||
        (control instanceof HTMLOptionElement &&
          (control.value === optionName || control.textContent?.trim() === optionName))
    );

    if (fallbackControl instanceof HTMLInputElement) {
      if (!fallbackControl.disabled && !fallbackControl.checked) fallbackControl.click();
      return;
    }

    if (fallbackControl instanceof HTMLOptionElement) {
      const fallbackSelect = fallbackControl.closest('select');
      if (!(fallbackSelect instanceof HTMLSelectElement) || fallbackControl.disabled) return;
      fallbackSelect.value = fallbackControl.value;
      fallbackSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Updates the sticky variant text immediately.
   * @param {ProductVariant} variant
   */
  #updateStickyVariantTitle(variant) {
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    if (!variantTitleElement) return;
    variantTitleElement.textContent = variant.title || variant.options.join(' / ');
  }

  /**
   * Gets the main product variant picker for this product section.
   * @returns {HTMLElement | null}
   */
  #getVariantPicker() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    const selector = `variant-picker[data-product-id="${productId}"]`;

    return sectionElement?.querySelector(selector) ?? document.querySelector(selector);
  }

  /**
   * Gets the product form element.
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
